/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionCopySmartSearchFilters';

const ActionCopySmartSearchFiltersManager: ActionManagerFn<
  'ActionCopySmartSearchFilters'
> = (platform: Platform): ActionManager<'ActionCopySmartSearchFilters'> => {
  return {
    ...baseActionManager(platform, 'ActionCopySmartSearchFilters'),
    defaultValue,
    props: {
      ...baseActionProps,
      copyFrom: {
        label: 'Copy from Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: ['ReadOnlyDataStore', 'DataStore', 'LocalDataStore'],
      },
      copyTo: {
        label: 'Copy to Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: ['ReadOnlyDataStore', 'DataStore', 'LocalDataStore'],
      },
    },
    name: 'Copy Smart Search Filters',
    icon: { iconName: 'copy' },
    description: `**Overview**<br/>
The Copy Record action is a data manipulation component designed to transfer or duplicate a single record between two distinct Data Store entities. It facilitates the movement of data across different scopes of the application, such as promoting data from a temporary local store to a persistent cloud store.<br/><br/>
This component manages the full lifecycle of the copy operation, handling the retrieval from the source and the insertion or update into the target. It includes specific logic (createAsNew) to determine whether the operation should treat the data as a fresh entry (stripping existing identifiers) or a direct clone.

**Key Features**<br/>
* **Bidirectional Store Compatibility:** Supports a wide array of store types for both source and target, including CloudDataStore, DataStore, LocalDataStore, TransientDataStore, and ReadOnlyDataStore.
* **Cloning Control:** Features a createAsNew boolean property that strips primary keys or unique identifiers from the source record, ensuring the target store treats it as a new insertion rather than an update.
* **Dual Dependency Tracking:** Implements isDataSourceAliasUsed to monitor both sourceAlias and targetAlias, ensuring the platform accurately tracks dependencies for both involved data entities.
* **Registry Readability:** The getDisplayText method formats the action label as Source -> Target, providing an immediate visual summary of the data flow direction in the editor.

**Properties**<br/>
* **Source Datastore Alias (Required):** A DataSourceAliasChooser property identifying the origin Data Store containing the record to be copied.
* **Target Datastore Alias (Required):** A DataSourceAliasChooser property identifying the destination Data Store where the record will be saved.
* **Create As New:** A boolean property.
  * *Function:* Determines if the record should be inserted as a completely new entry (true) or potentially maintain its identity/state (false).

**Use Cases**<br/>
* **Draft to Live:** Copying a record from a TransientDataStore (used for a form draft) to a CloudDataStore (persistent database) upon submission.
* **Template Duplication:** Creating a new record based on an existing one by copying from a source store to itself (or another) with createAsNew enabled.`,
  };
};

export default ActionCopySmartSearchFiltersManager;
